import {
  collection,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { enrichLeadWithAutoDistribution } from './leadAutoDistribution';
import { syncLeadToPipeline } from './syncLeadToPipeline';
import { trackGenerateLead } from '../services/analyticsService';

const INTERESSE_LABELS = {
  compra: 'Compra',
  venda: 'Venda',
  aluguel: 'Aluguel',
  locacao: 'Locação',
  avaliacao: 'Avaliação',
  outros: 'Outros'
};

const ORCAMENTO_LABELS = {
  'ate-100k': 'Até R$ 100.000',
  '100k-200k': 'R$ 100.000 - R$ 200.000',
  '200k-300k': 'R$ 200.000 - R$ 300.000',
  '300k-500k': 'R$ 300.000 - R$ 500.000',
  '500k-1m': 'R$ 500.000 - R$ 1.000.000',
  'acima-1m': 'Acima de R$ 1.000.000'
};

function mapInteresseToLeadType(interesse) {
  switch (interesse) {
    case 'compra':
      return 'buy';
    case 'venda':
      return 'sell';
    case 'aluguel':
    case 'locacao':
      return 'rent';
    case 'avaliacao':
      return 'avaliacao';
    case 'outros':
      return 'outros';
    default:
      return 'help';
  }
}

function updateStageHistory(currentStageHistory, newStatus) {
  const normalizeStatus = (status) => {
    if (!status) return 'lead';
    const statusLower = String(status).toLowerCase().trim();
    if (statusLower === 'novo' || statusLower === 'lead') return 'lead';
    if (statusLower === 'qualificado') return 'qualificado';
    if (statusLower === 'proposta' || statusLower === 'proposta enviada') return 'proposta';
    if (statusLower === 'negociacao' || statusLower === 'negociação') return 'negociacao';
    if (statusLower === 'ganho' || statusLower === 'convertido') return 'ganho';
    if (statusLower === 'perdido') return 'perdido';
    return statusLower;
  };

  const normalizedStatus = normalizeStatus(newStatus);
  const history = Array.isArray(currentStageHistory) ? [...currentStageHistory] : [];
  if (history.length === 0 || history[history.length - 1].stage !== normalizedStatus) {
    history.push({
      stage: normalizedStatus,
      timestamp: new Date()
    });
  }
  return history;
}

/**
 * Cria lead no CRM a partir do formulário de /contato.
 */
export async function createContactLead({
  name,
  phone,
  email,
  message,
  interesse,
  orcamento
}) {
  const interesseLabel = INTERESSE_LABELS[interesse] || interesse || '';
  const orcamentoLabel = ORCAMENTO_LABELS[orcamento] || orcamento || '';

  let descricao = (message || '').trim();
  const metaLines = [
    'Origem: Formulário de contato (/contato)',
    interesseLabel ? `Interesse: ${interesseLabel}` : null,
    orcamentoLabel ? `Orçamento: ${orcamentoLabel}` : null
  ].filter(Boolean);

  if (metaLines.length) {
    descricao = [descricao, '', '—', ...metaLines].filter(Boolean).join('\n');
  }

  const initialHistory = [
    {
      action: 'lead_criado',
      description: 'Lead criado via formulário de contato',
      performedBy: 'system',
      performedByName: 'Site',
      timestamp: new Date(),
      createdAt: new Date(),
      metadata: {
        source: 'contact',
        interesse: interesse || '',
        orcamento: orcamento || ''
      }
    }
  ];

  const initialStageHistory = updateStageHistory([], 'novo');

  let leadData = {
    name: name.trim(),
    phone: phone.trim(),
    email: (email && email.trim()) || null,
    type: mapInteresseToLeadType(interesse),
    status: 'novo',
    propertyCode: null,
    descricao,
    adminNotes: '',
    source: 'contact',
    interesse: interesse || null,
    orcamento: orcamento || null,
    history: initialHistory,
    stageHistory: initialStageHistory,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  leadData = await enrichLeadWithAutoDistribution(db, leadData);

  try {
    const { submitPublicLeadViaFunction } = await import('../services/secureCallable');
    const { leadId } = await submitPublicLeadViaFunction({
      ...leadData,
      createdAt: undefined,
      updatedAt: undefined,
      message: leadData.descricao,
      notes: leadData.descricao,
    });
    trackGenerateLead({
      source: 'contact_form',
      itemType: interesse || 'contact'
    });
    return leadId;
  } catch (err) {
    console.warn('submitPublicLead via Function falhou, fallback addDoc:', err?.message || err);
    const docRef = await addDoc(collection(db, 'leads'), leadData);
    await syncLeadToPipeline({ ...leadData, id: docRef.id }, docRef.id);
    trackGenerateLead({
      source: 'contact_form',
      itemType: interesse || 'contact'
    });
    return docRef.id;
  }
}
